import { isDefined, isNotDefined } from '@sgme/fp';
import { HStack, Spacer, VStack } from '@sgme/ui';
import { format } from 'date-fns';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import {
  useAlgoIsChecking,
  useAlgoIsModifyingField,
  useAlgoTileId,
  useValidateAlgo,
} from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { AlgoEditingAlphaSeeker } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingAlphaSeeker.tsx';
import { AlgoEditingClippingMode } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingClippingMode.tsx';
import { AlgoEditingClipSize } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingClipSize.tsx';
import { AlgoEditingExpiryDate } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingExpiryDate.tsx';
import { AlgoEditingLimitPrice } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingLimitPrice.tsx';
import AlgoEditingLiquidityPool from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingLiquidityPool.tsx';
import { AlgoEditingNotional } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingNotional.tsx';
import { AlgoEditingRandomize } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingRandomize.tsx';
import { AlgoEditingSpeed } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingSpeed.tsx';
import { AlgoEditingSpreadCapture } from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingSpreadCapture.tsx';
import AlgoEditingStartDate from '@/App/tiles/orders/AlgoOrders/AlgoOrderEditingTile/AlgoEditingStartDate';
import AlgoActionButtons from '@/App/tiles/orders/AlgoOrders/AlgoOrderExecutedTile/AlgoActionButtons';
import { ConnectedAlgoOrderSpot } from '@/App/tiles/orders/AlgoOrders/ConnectedAlgoOrderSpot.tsx';
import ActionButtonsForEditOrders from '@/App/tiles/orders/components/ActionButtonsForEditOrders.tsx';
import { ExecutedTileField } from '@/App/tiles/orders/components/ExecutedTileField.tsx';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { ProgressBar } from '@/App/tiles/orders/components/ProgressBar.tsx';
import { Summary } from '@/App/tiles/orders/components/Summary.tsx';
import { ExecutionPendingOrderStatus } from '@/App/tiles/orders/ExecutionPendingOrderStatus.tsx';
import { useAccount } from '@/App/tiles/orders/hooks.tsx';
import { getStatusClass, mapStatusToColor } from '@/App/tiles/orders/utils.ts';
import { AutoAdvanceForm } from '@/components/AutoAdvanceForm.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { finalStatuses } from '@/store/bulkOrder/state.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { useUpdateAlgoMutation } from '@/store/orders/algoOrders/api/index.ts';
import { selectAlgoTile, selectTradeIdFromAlgoTile } from '@/store/orders/algoOrders/selectors.ts';
import type { ClippingMode, ExpiryDate, Speed, StartDate } from '@/store/orders/algoOrders/state.ts';
import { selectOrderEntryByIdFromBlotter } from '@/store/orders/fixing/selectors.ts';
import type { FixingPriceType } from '@/store/orders/fixing/state.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import type { AlgoOrderType, ValidationResponseDetailContent } from '@/store/orders/state.ts';
import { algoOrderTypes } from '@/store/orders/state.ts';
import { removeTile } from '@/store/tiles/slice.ts';

type AlgoOrderEditingTileProps = {
  algoType: AlgoOrderType;
};

export const AlgoOrderEditingTile = (props: AlgoOrderEditingTileProps) => {
  const track = useTrackEvent();
  const { algoType } = props;
  const tileId = useAlgoTileId();
  const isChecking = useAlgoIsChecking();
  const isModifyingField = useAlgoIsModifyingField();

  const dispatch = useAppDispatch();
  const userContextValue = useUserContext();

  const [notional, setNotional] = useState<number | undefined>(undefined);
  const [expiryDate, setExpiryDate] = useState<ExpiryDate | undefined>(undefined);
  const [limitPrice, setLimitPrice] = useState<number | undefined>(undefined);
  const [clipSize, setClipSize] = useState<number | undefined>(undefined);
  const [clippingMode, setClippingMode] = useState<ClippingMode | undefined>(undefined);
  const [randomize, setRandomize] = useState<boolean | undefined>(undefined);
  const [spreadCapture, setSpreadCapture] = useState<boolean | undefined>(undefined);
  const [speed, setSpeed] = useState<Speed | undefined>(undefined);
  const [alphaSeeker, setAlphaSeeker] = useState<boolean | undefined>(undefined);
  const [selectedAlgoType, setSelectedAlgoType] = useState<AlgoOrderType>(algoType);

  const validateAlgo = useValidateAlgo();

  const [updateAlgo] = useUpdateAlgoMutation({
    selectFromResult: () => ({}),
  });

  const tradeId = useAppSelector((state) => selectTradeIdFromAlgoTile(state, tileId));
  const tile = useAppSelector((state) => selectAlgoTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const canTrade = checkIfUserCanTrade(userContextValue);

  const isDisabled = isNotDefined(tile) || !canTrade || isModifyingField;

  const account = useAccount(orderFromBlotter);

  const isPaused = orderFromBlotter?.status === 'Paused';
  const isFinal = finalStatuses.includes(orderFromBlotter?.status ?? '');
  const color = mapStatusToColor(orderFromBlotter?.status);

  const handleAlgoTypeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const newType = event.target.value as AlgoOrderType;

    const response = await validateAlgo({}, newType);

    if (response?.data?.validationResponseDetailContent?.length === 0) {
      setSelectedAlgoType(newType);
      setClippingMode(undefined);
      setClipSize(undefined);
      setRandomize(undefined);
      setSpreadCapture(undefined);
      setSpeed(undefined);
      setAlphaSeeker(undefined);
    }
  };

  function handleClose() {
    track('Order action', 'Remove Order');
    dispatch(removeTile(tileId));
  }

  function cancelChange() {
    track('Order action', 'Cancel editing Order');
    dispatch(ordersSlice.actions.changeOrderMode({ mode: 'VIEW', tileId }));
  }

  const amendChanges = () => {
    track('Order action', 'Amend Order');
    if (isNotDefined(orderFromBlotter)) {
      console.error(`[handleSubmit] Tile with id ${tileId} not found in state.`);
      return;
    }

    const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
      ? orderFromBlotter.currencyPair.split('/')
      : ['', ''];

    if (isNotDefined(currency1) || isNotDefined(currency2) || isNotDefined(tradeId)) {
      console.error(`[handleValidateAlgo] Invalid currency pair: ${currency1}/${currency2}`);
      return;
    }

    const startDate = buildStartDate(orderFromBlotter.startTime ?? undefined);
    const expiryDateValue = buildExpiryDate(orderFromBlotter.endTime ?? undefined, orderFromBlotter.isGtc, expiryDate);
    const typeSpecificFields = buildTypeSpecificFields(
      selectedAlgoType,
      { clippingMode, clipSize, randomize, spreadCapture, speed, alphaSeeker },
      {
        clippingMode: orderFromBlotter.clippingMode ?? undefined,
        clipSize: orderFromBlotter.clipSize ?? undefined,
        randomize: orderFromBlotter.randomize ?? undefined,
        spreadCapture: orderFromBlotter.spreadCapture ?? undefined,
        speed: orderFromBlotter.speed ?? undefined,
        alphaSeeker: orderFromBlotter.alphaSeeker ?? undefined,
      },
    );

    updateAlgo({
      tileId,
      orderRef: tradeId,
      orderType: selectedAlgoType,
      currency1,
      currency2,
      way: orderFromBlotter?.yourWay === 'Buy' ? 'buy' : 'sell',
      notional: isDefined(notional) ? notional : (orderFromBlotter.notional ?? 0),
      notionalCurrencyIndex: orderFromBlotter.notionalCurrency === currency1 ? 1 : 2,
      limitPrice: isDefined(limitPrice) ? limitPrice : orderFromBlotter.limitPrice,
      startDate,
      expiryDate: expiryDateValue,
      liquidityPool: tile?.properties.liquidityPool ?? [],
      ...typeSpecificFields,
    })
      .unwrap()
      .then(() => {
        track('Order action', 'Amend Order successful');
        dispatch(ordersSlice.actions.changeOrderMode({ mode: 'VIEW', tileId }));
      })
      .catch(({ errors }: { type: 'failure'; errors: ValidationResponseDetailContent[] }) => {
        console.error('Failed to amend order:', errors);
        if (errors.some((error) => error.errorDescription === 'timeout')) {
          const params: SendHelpRequestRequest = {
            config: 'orderpending',
            details: JSON.stringify({
              payload: errors,
              orderIdToUpdate: tileId,
            }),
            data: {
              orderDescription: 'creation',
              orderType: 'limit',
              orderCurrencyType: 'unknown', // send 'g10' or 'emerging' once differentiation is supported
              orderId: tileId,
            },
            templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
            templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
          };

          logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
          dispatch(sendHelpRequestThunk(params));
        }
      });
  };

  if (isNotDefined(orderFromBlotter)) {
    logger.logInformation(`[ORDER] Tile with id ${tileId} not found in blotter while editing.`);
    return <ExecutionPendingOrderStatus />;
  }

  return (
    <AutoAdvanceForm className="bg-lvl1 w-100 h-100">
      <VStack className="bg-lvl1" verticalAlignment="start" fullWidth fullHeight>
        <HStack className="m-2">
          <div>
            <span className="fw-bold">Order {tradeId} </span>
            <span className={getStatusClass(orderFromBlotter?.status)}>{orderFromBlotter?.status}</span>
          </div>
          <button type="button" className="btn btn-flat-primary" onClick={handleClose} data-e2e="close-tile">
            <em className="icon icon-sm">close</em>
          </button>
        </HStack>

        <div className={`bg-${color} bg-opacity-10 text-${color} p-3 my-3`}>
          <ProgressBar
            executedAmount={orderFromBlotter?.executedAmount}
            notional={orderFromBlotter?.notional}
            color={color}
            isFinal={isFinal}
          />

          <div className="my-3">
            <span className="summary-text">
              <Summary
                account={account}
                orderType={orderFromBlotter?.orderType ?? 'Call'}
                way={orderFromBlotter?.yourWay ?? null}
                isGtc={tile?.properties.expiryDate.goodTillDate ?? false}
                notional={notional ?? orderFromBlotter?.notional ?? null}
                currency={orderFromBlotter?.notionalCurrency ?? null}
                limitPrice={limitPrice ?? orderFromBlotter?.limitPrice ?? null}
                expiryDay={expiryDate?.date ?? orderFromBlotter?.expiryDay ?? null}
                expiryTime={expiryDate?.time ?? orderFromBlotter?.expiryTime ?? null}
                currencyPair={orderFromBlotter?.currencyPair ?? null}
                fixingPlaceOrTime={orderFromBlotter?.fixingTime ?? orderFromBlotter?.fixingPlace ?? null}
                fixingDate={orderFromBlotter?.fixingDateUtc ?? null}
                fixingRef={orderFromBlotter?.fixingBenchmark ?? null}
                priceType={(orderFromBlotter?.fixingPriceType as FixingPriceType) ?? null}
                finalStatus={isFinal}
              />
            </span>
          </div>
          <AlgoActionButtons canEditOrder tradeId={tradeId} isPaused={isPaused} algoType={selectedAlgoType} />
        </div>

        <div>
          <ExecutedTileField label={'currencyPair'} value={orderFromBlotter?.currencyPair} />
          <ExecutedTileField label={'way'} value={orderFromBlotter?.yourWay as string} />
          <ExecutedTileField label="averagePrice" value={orderFromBlotter?.averagePrice} isLast />
        </div>

        <span className="text-muted m-8px">
          <FormattedMessage id="order.result.editableAttributes" />
        </span>
        <div
          className="mx-16px gap-8px"
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto minmax(0, 1fr)',
            gridTemplateRows: '20px',
            alignItems: 'center',
          }}
        >
          <ConnectedAlgoOrderSpot />
          <OrderRow labelId="order.fields.algoType">
            <Form.Select value={selectedAlgoType} onChange={handleAlgoTypeChange} size="sm" data-e2e="algo-type-select">
              {algoOrderTypes.map((type) => (
                <option key={type} value={type}>
                  <FormattedMessage id={`order.fields.algoType.${type}`} />
                </option>
              ))}
            </Form.Select>
          </OrderRow>
          <AlgoEditingNotional notional={notional} setNotional={setNotional} />
          <AlgoEditingStartDate />
          <AlgoEditingExpiryDate expiryDate={expiryDate} setExpiryDate={setExpiryDate} />
          <AlgoEditingLimitPrice limitPrice={limitPrice} setLimitPrice={setLimitPrice} />
          {selectedAlgoType === 'Twap+Order' && (
            <>
              <AlgoEditingClippingMode clippingMode={clippingMode} setClippingMode={setClippingMode} />
              {(clippingMode === 'Manual' ||
                (isNotDefined(clippingMode) && orderFromBlotter.clippingMode === 'Manual')) && (
                <AlgoEditingClipSize clipSize={clipSize} setClipSize={setClipSize} />
              )}
              <AlgoEditingRandomize randomize={randomize} setRandomize={setRandomize} />
              <AlgoEditingSpreadCapture spreadCapture={spreadCapture} setSpreadCapture={setSpreadCapture} />
            </>
          )}
          {selectedAlgoType === 'NightjarOrder' && (
            <>
              <AlgoEditingSpeed speed={speed} setSpeed={setSpeed} />
              <AlgoEditingAlphaSeeker alphaSeeker={alphaSeeker} setAlphaSeeker={setAlphaSeeker} />
            </>
          )}

          {selectedAlgoType === 'FalconOrder' && <AlgoEditingSpeed speed={speed} setSpeed={setSpeed} />}
          <AlgoEditingLiquidityPool />
        </div>
        <Spacer />
        <ActionButtonsForEditOrders cancelChange={cancelChange} amendChanges={amendChanges} disabled={isDisabled} />
      </VStack>
      {isChecking && (
        <div className="position-absolute z-3 top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-lvl1 bg-opacity-75">
          <div className="spinner blinker-lg" />
        </div>
      )}
    </AutoAdvanceForm>
  );
};

function buildStartDate(startTime: string | undefined): StartDate {
  if (isNotDefined(startTime)) {
    return { date: undefined, time: undefined, isNow: false };
  }
  const date = new Date(startTime);
  return { date: format(date, 'yyyy-MM-dd'), time: format(date, 'HH:mm:ss'), isNow: false };
}

function buildExpiryDate(
  endTime: string | undefined,
  isGtc: boolean,
  localExpiryDate: ExpiryDate | undefined,
): ExpiryDate {
  if (isDefined(localExpiryDate)) {
    return localExpiryDate;
  }
  if (isNotDefined(endTime)) {
    return { date: undefined, time: undefined, goodTillDate: false };
  }
  const date = new Date(endTime);
  return { date: format(date, 'yyyy-MM-dd'), time: format(date, 'HH:mm:ss'), goodTillDate: isGtc };
}

type TypeSpecificLocalState = {
  clippingMode: ClippingMode | undefined;
  clipSize: number | undefined;
  randomize: boolean | undefined;
  spreadCapture: boolean | undefined;
  speed: Speed | undefined;
  alphaSeeker: boolean | undefined;
};

type BlotterTypeSpecificFields = {
  clippingMode?: string;
  clipSize?: number;
  randomize?: boolean;
  spreadCapture?: boolean;
  speed?: string;
  alphaSeeker?: boolean;
};

function buildTypeSpecificFields(
  algoType: AlgoOrderType,
  local: TypeSpecificLocalState,
  blotter: BlotterTypeSpecificFields,
) {
  const isTwap = algoType === 'Twap+Order';
  const isNightjar = algoType === 'NightjarOrder';

  const getClippingMode = (): ClippingMode => {
    if (!isTwap) return 'Automatic';
    if (isDefined(local.clippingMode)) return local.clippingMode;
    return (blotter.clippingMode as ClippingMode) ?? 'Automatic';
  };

  const getSpreadCapture = (): boolean => {
    if (!isTwap) return false;
    if (isDefined(local.spreadCapture)) return local.spreadCapture;
    return blotter.spreadCapture ?? false;
  };

  const getClipSize = (): number | undefined => {
    if (!isTwap) return undefined;
    if (isDefined(local.clipSize)) return local.clipSize;
    return blotter.clipSize;
  };

  const getRandomize = (): boolean => {
    if (!isTwap) return false;
    if (isDefined(local.randomize)) return local.randomize;
    return blotter.randomize ?? false;
  };

  const getSpeed = (): Speed | undefined => {
    if (isTwap) return undefined;
    if (isDefined(local.speed)) return local.speed;
    return (blotter.speed as Speed | undefined) ?? 'Normal';
  };

  const getAlphaSeeker = (): boolean => {
    if (!isNightjar) return false;
    if (isDefined(local.alphaSeeker)) return local.alphaSeeker;
    return blotter.alphaSeeker ?? false;
  };

  return {
    clippingMode: getClippingMode(),
    spreadCapture: getSpreadCapture(),
    clipSize: getClipSize(),
    randomize: getRandomize(),
    speed: getSpeed(),
    alphaSeeker: getAlphaSeeker(),
  };
}
